import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { MeterReading, MeterReadingInsert, MeterReadingUpdate } from '../types'

const QUERY_KEY = 'meter_readings'

export function useMeterReadings(distributionKeyId?: string, fiscalYearId?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, distributionKeyId, fiscalYearId],
    queryFn: async () => {
      let query = supabase
        .from('meter_readings')
        .select(`
          *,
          unit:units (id, unit_number, stairwell, floor),
          fiscal_year:fiscal_years (id, year)
        `)
        .order('unit_id')
        .order('period_start')

      if (distributionKeyId) query = query.eq('distribution_key_id', distributionKeyId)
      if (fiscalYearId) query = query.eq('fiscal_year_id', fiscalYearId)

      const { data, error } = await query
      if (error) throw error
      return data
    },
    enabled: !!distributionKeyId,
  })
}

export function useMeterReading(id?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, 'detail', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('meter_readings')
        .select(`
          *,
          unit:units (id, unit_number, stairwell, floor),
          fiscal_year:fiscal_years (id, year)
        `)
        .eq('id', id!)
        .single()

      if (error) throw error
      return data
    },
    enabled: !!id,
  })
}

export function useCreateMeterReading() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: Omit<MeterReadingInsert, 'id' | 'created_at' | 'updated_at'>) => {
      // Calculer consumption si index fournis
      let consumption = data.consumption
      if (data.index_start != null && data.index_end != null && !consumption) {
        consumption = Number(data.index_end) - Number(data.index_start)
      }

      const { data: result, error } = await supabase
        .from('meter_readings')
        .insert({ ...data, consumption: consumption! })
        .select()
        .single()
      if (error) throw error
      return result as MeterReading
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

export function useUpdateMeterReading() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<MeterReadingUpdate> & { id: string }) => {
      // Recalculer consumption si index modifiés
      if (data.index_start !== undefined || data.index_end !== undefined) {
        const { data: existing } = await supabase
          .from('meter_readings')
          .select('index_start, index_end')
          .eq('id', id)
          .single()

        const newStart = data.index_start ?? existing?.index_start ?? 0
        const newEnd = data.index_end ?? existing?.index_end ?? 0
        data.consumption = Number(newEnd) - Number(newStart)
      }

      const { data: result, error } = await supabase
        .from('meter_readings')
        .update(data)
        .eq('id', id)
        .select()
        .single()
      if (error) throw error
      return result as MeterReading
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

export function useDeleteMeterReading() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('meter_readings').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

export function useBulkImportMeterReadings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (
      readings: Array<Omit<MeterReadingInsert, 'id' | 'created_at' | 'updated_at'>>
    ) => {
      const { error } = await supabase.from('meter_readings').insert(readings)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
    },
  })
}

// Hook pour vérifier si une clé variable a des relevés
export function useHasReadings(distributionKeyId?: string, fiscalYearId?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, 'hasReadings', distributionKeyId, fiscalYearId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('meter_readings')
        .select('*', { count: 'exact', head: true })
        .eq('distribution_key_id', distributionKeyId!)
        .eq('fiscal_year_id', fiscalYearId!)

      if (error) throw error
      return (count ?? 0) > 0
    },
    enabled: !!distributionKeyId && !!fiscalYearId,
  })
}

// Hook pour compter les relevés manquants
export function useCountMissingReadings(distributionKeyId?: string, fiscalYearId?: string) {
  return useQuery({
    queryKey: [QUERY_KEY, 'missing', distributionKeyId, fiscalYearId],
    queryFn: async () => {
      // Compter les unités incluses dans la clé
      const { count: totalUnits, error: unitsError } = await supabase
        .from('distribution_key_units')
        .select('*', { count: 'exact', head: true })
        .eq('distribution_key_id', distributionKeyId!)
        .eq('is_included', true)

      if (unitsError) throw unitsError

      // Compter les relevés existants
      const { count: readingsCount, error: readingsError } = await supabase
        .from('meter_readings')
        .select('*', { count: 'exact', head: true })
        .eq('distribution_key_id', distributionKeyId!)
        .eq('fiscal_year_id', fiscalYearId!)

      if (readingsError) throw readingsError

      return {
        total: totalUnits ?? 0,
        withReadings: readingsCount ?? 0,
        missing: (totalUnits ?? 0) - (readingsCount ?? 0),
      }
    },
    enabled: !!distributionKeyId && !!fiscalYearId,
  })
}
